/**
 * /launcher — HumanAI hub picker.
 *
 * เป็นหน้า "ตู้ม้ๆ" ของ owner: หลัง web login เสร็จมาเจอที่นี่ก่อน,
 * เลือก app ที่อยากทำงาน. คลิก PayPilot = navigate ภายในเดิม;
 * คลิก JobPilot / EvalPilot = redirect ไป URL ของ app นั้นพร้อม
 * แนบ JWT ใน hash (`#token=...`) ให้ app ปลายทางอ่านแล้ว setAuth ต่อ.
 *
 * Cross-app session ใช้ได้ก็ต่อเมื่อ 3 worker ตั้ง WORKER_JWT_SECRET
 * เดียวกัน (ดู CONTEXT/INTEGRATION-DEPLOY.md). ถ้า env ของ
 * VITE_JOBPILOT_URL / VITE_EVALPILOT_URL ยังว่าง จะ disable card นั้น
 * (ขึ้น "เร็วๆ นี้") แทนพา user ไป URL ที่ผิด.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ExternalLink, ArrowRight, UserPlus, X, Copy, CheckCircle2 } from 'lucide-react';
import { useAuthStore } from '../lib/store';
import { Logo } from '../components/ui/Logo';
import { apiFetch } from '../lib/api-client';
import { TenantSwitcher } from '../components/layout/TenantSwitcher';

const JOBPILOT_URL = (import.meta.env.VITE_JOBPILOT_URL ?? 'https://jobpilot-admin-liff.pages.dev').trim();
const EVALPILOT_URL = (import.meta.env.VITE_EVALPILOT_URL ?? 'https://eval-pilot-react.pages.dev').trim();

type SubStatus = 'active' | 'trial' | 'cancelled' | 'expired' | 'none' | 'loading' | 'requested' | 'suspended' | 'rejected';

interface ProductBadge { status: SubStatus; days_left?: number | null; }

interface AppCard {
  key: 'paypilot' | 'jobpilot' | 'evalpilot';
  name: string;
  tagline: string;
  href: string | null;          // null = external not configured
  internal: boolean;             // true = same-origin <Link>
  accent: string;
}

interface InviteAdminResult {
  emp_code: string;
  pairing_code: string;
  tenant_code: string | null;
  role: string;
  expires_at: string;
}

type InviteRole = 'OWNER' | 'HR' | 'MANAGER';

const ROLE_LABEL: Record<InviteRole, string> = {
  OWNER: 'เจ้าของกิจการ',
  HR: 'HR / ฝ่ายบุคคล',
  MANAGER: 'ผู้จัดการ',
};

function InviteAdminModal({ onClose }: { onClose: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('OWNER');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteAdminResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await apiFetch<InviteAdminResult>('/owner/invite-admin', {
        method: 'POST',
        body: { first_name: firstName, last_name: lastName, email, role },
      });
      setResult(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">เชิญผู้ดูแล</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              เชิญ Owner / HR / Manager — ระบบสร้างรหัสจับคู่ให้ ส่งโค้ดให้คนนั้นไปผูก LINE
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">บทบาท *</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as InviteRole)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-400"
              >
                {(Object.keys(ROLE_LABEL) as InviteRole[]).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">ชื่อ *</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">นามสกุล *</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">อีเมล *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="owner@example.com"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-400"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg">
                ยกเลิก
              </button>
              <button type="submit" disabled={submitting} className="flex-1 py-2 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50">
                {submitting ? 'กำลังสร้าง...' : 'สร้างรหัสจับคู่'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <CheckCircle2 className="w-4 h-4" />
              สร้างคำเชิญเรียบร้อย ({ROLE_LABEL[result.role as InviteRole] ?? result.role}) — ส่ง 2 รหัสนี้ให้ผู้ที่ได้รับเชิญ
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">รหัสบริษัท (Tenant Code)</div>
              <div className="mt-1 flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-navy-700 tracking-wider">{result.tenant_code ?? '—'}</code>
                <button
                  onClick={() => copy(result.tenant_code ?? '', 'tenant')}
                  className="p-1.5 rounded hover:bg-slate-100"
                  title="คัดลอก"
                >
                  {copied === 'tenant' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">รหัสจับคู่ (Pairing Code)</div>
              <div className="mt-1 flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-brand-700 tracking-wider">{result.pairing_code}</code>
                <button
                  onClick={() => copy(result.pairing_code, 'code')}
                  className="p-1.5 rounded hover:bg-slate-100"
                  title="คัดลอก"
                >
                  {copied === 'code' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
                </button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                หมดอายุ {new Date(result.expires_at).toLocaleString('th-TH')}
              </div>
            </div>

            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <strong>วิธีใช้:</strong> ส่ง 2 รหัสนี้ให้ผู้ได้รับเชิญ → ให้เปิด LIFF PayPilot
              → กด "ฉันมีรหัสเชิญ" → กรอกรหัสบริษัท + รหัสจับคู่ → ผูก LINE → เข้าใช้งานเป็น {ROLE_LABEL[result.role as InviteRole] ?? result.role} ได้ทันที
            </div>

            <button onClick={onClose} className="w-full py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg">
              ปิด
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// === Team Directory (Owner only) — list users + product access toggles ===
interface TeamMember {
  user_id: string;
  display_name: string | null;
  role: string;
  emp_code: string | null;
  line_user_id: string;
  status: string;
  products: Array<{ product: string; status: string; product_role: string }>;
}

const PRODUCT_LABEL: Record<string, string> = {
  paypilot: 'PayPilot',
  jobpilot: 'JobPilot',
  evalpilot: 'EvalPilot',
};

function TeamDirectory() {
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState<string>('');
  const [reload, setReload] = useState(0);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  useEffect(() => {
    setMembers(null);
    setError('');
    apiFetch<{ members: TeamMember[] }>('/owner/team-members')
      .then((d) => setMembers(d.members ?? []))
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setMembers([]);
      });
  }, [reload]);

  const toggleAccess = async (userId: string, product: string, currentlyActive: boolean) => {
    const key = `${userId}:${product}`;
    setPendingKey(key);
    try {
      await apiFetch(`/owner/team-members/${userId}/access`, {
        method: 'POST',
        body: {
          product,
          action: currentlyActive ? 'revoke' : 'grant',
        },
      });
      setReload((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingKey(null);
    }
  };

  if (members === null) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
        กำลังโหลดรายชื่อ...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
        {error}
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-slate-500">
        ยังไม่มีสมาชิกในบริษัท — กดปุ่ม "เชิญผู้ดูแล" ด้านบนเพื่อเพิ่ม
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
      {members.map((m) => {
        const accessMap = new Map(m.products.map((p) => [p.product, p]));
        return (
          <div key={m.user_id} className="px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-bright to-gold text-ink flex items-center justify-center text-xs font-bold shrink-0">
              {(m.display_name ?? m.emp_code ?? '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">
                {m.display_name ?? '(ยังไม่ผูก LINE)'}
              </div>
              <div className="text-[11px] text-slate-500">
                {m.emp_code ?? '—'} · {m.role}
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-1">
              {(['paypilot', 'jobpilot', 'evalpilot'] as const).map((p) => {
                const access = accessMap.get(p);
                const isActive = access?.status === 'active';
                const key = `${m.user_id}:${p}`;
                const pending = pendingKey === key;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggleAccess(m.user_id, p, isActive)}
                    disabled={pending}
                    className={
                      'text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors disabled:opacity-50 ' +
                      (isActive
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                        : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100')
                    }
                    title={`${PRODUCT_LABEL[p]} — ${isActive ? 'มีสิทธิ์ (คลิกเพื่อยกเลิก)' : 'ไม่มีสิทธิ์ (คลิกเพื่อให้)'}`}
                  >
                    {PRODUCT_LABEL[p]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Launcher() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const jwt = useAuthStore((s) => s.jwt);
  const clear = useAuthStore((s) => s.clear);
  const [inviteOpen, setInviteOpen] = useState(false);
  const isOwner = profile?.role === 'OWNER';

  const tokenHash = jwt ? `#token=${encodeURIComponent(jwt)}` : '';

  // Fetch all products' subscription status (shows badge on cards)
  const [badges, setBadges] = useState<Record<string, ProductBadge>>({
    evalpilot: { status: 'loading' },
    jobpilot: { status: 'loading' },
  });
  function loadBadges() {
    apiFetch<{ products: Record<string, { status: string; days_remaining?: number | null }> }>('/me/products')
      .then((r) => {
        const products = r?.products ?? {};
        const next: Record<string, ProductBadge> = {};
        for (const p of ['evalpilot', 'jobpilot']) {
          const info = products[p];
          if (!info) next[p] = { status: 'none' };
          else next[p] = { status: info.status as SubStatus, days_left: info.days_remaining ?? null };
        }
        setBadges(next);
      })
      .catch(() => setBadges({ evalpilot: { status: 'none' }, jobpilot: { status: 'none' } }));
  }
  useEffect(() => {
    if (!jwt) return;
    loadBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  const cards: AppCard[] = [
    {
      key: 'paypilot',
      name: 'PayPilot',
      tagline: 'เงินเดือน · ลา · OT · เช็คอิน',
      href: '/',
      internal: true,
      accent: 'from-gold-bright to-gold',
    },
    {
      key: 'jobpilot',
      name: 'JobPilot',
      tagline: 'รับสมัครงาน · สัมภาษณ์ · onboarding',
      href: JOBPILOT_URL ? `${JOBPILOT_URL}${tokenHash}` : null,
      internal: false,
      accent: 'from-ink to-gold-deep',
    },
    {
      key: 'evalpilot',
      name: 'EvalPilot',
      tagline: 'ประเมินผล · skill matrix · KPI',
      href: EVALPILOT_URL ? `${EVALPILOT_URL}${tokenHash}` : null,
      internal: false,
      accent: 'from-gold-deep to-ink',
    },
  ];

  function handleLogout() {
    clear();
    navigate('/login', { replace: true });
  }

  const [requesting, setRequesting] = useState<string | null>(null);
  async function requestAccess(product: 'jobpilot' | 'evalpilot') {
    setRequesting(product);
    try {
      await apiFetch(`/me/products/${product}/request`, { method: 'POST', body: {} });
      setBadges((prev) => ({ ...prev, [product]: { status: 'requested' } }));
    } catch {
      // ignore — badge stays as-is; user can retry
    } finally {
      setRequesting(null);
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <img src="/sukree-logo.png" alt="สุกรีเภสัช" className="h-12 w-auto" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <div className="text-sm font-medium text-right">{profile?.display_name ?? profile?.emp_code ?? ''}</div>
              <div className="w-52"><TenantSwitcher /></div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              title="ออกจากระบบ"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
        <h1 className="text-2xl font-semibold mb-1">เลือกแอปที่ต้องการใช้งาน</h1>
        <p className="text-sm text-gray-500 mb-8">
          บัญชีเดียวเข้าได้ทั้ง 3 แอป — กลับมาที่หน้านี้ได้ตลอดจากปุ่ม "เปลี่ยนแอป" ใน sidebar
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map((c) => {
            const disabled = !c.href;
            const inner = (
              <article
                className={`card p-5 h-full flex flex-col group transition ${
                  disabled ? 'opacity-60' : 'hover:shadow-md cursor-pointer'
                }`}
              >
                {/* Logo block: gradient-tinted square so each product still
                    reads as distinct even with the shared สุกรีเภสัช mark. */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center mb-3 p-2`}>
                  <Logo white size="h-8" />
                </div>
                <div className="font-semibold text-lg">{c.name}</div>
                <p className="text-sm text-gray-500 flex-1 mt-1">{c.tagline}</p>

                {(c.key === 'evalpilot' || c.key === 'jobpilot') && (() => {
                  const b = badges[c.key];
                  if (!b || b.status === 'loading') return <div className="text-[10px] text-gray-400 mt-2">กำลังเช็คสถานะ...</div>;
                  if (b.status === 'active')    return <span className="inline-block text-[10px] mt-2 px-2 py-0.5 rounded font-medium bg-emerald-50 text-emerald-700">เปิดใช้งาน</span>;
                  if (b.status === 'trial')     return <span className="inline-block text-[10px] mt-2 px-2 py-0.5 rounded font-medium bg-amber-50 text-amber-700">ทดลอง · เหลือ {b.days_left} วัน</span>;
                  if (b.status === 'requested') return <span className="inline-block text-[10px] mt-2 px-2 py-0.5 rounded font-medium bg-amber-50 text-amber-700">รออนุมัติจากทีมงาน</span>;
                  if (b.status === 'suspended') return <span className="inline-block text-[10px] mt-2 px-2 py-0.5 rounded font-medium bg-rose-50 text-rose-700">ถูกระงับการใช้งาน</span>;
                  // none / expired / cancelled / rejected → let the Owner request access
                  const verb = b.status === 'expired' || b.status === 'cancelled' ? 'ขอเปิดใช้อีกครั้ง' : 'ขอใช้งาน · ทดลองฟรี 14 วัน';
                  return (
                    <button
                      type="button"
                      disabled={requesting === c.key}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        requestAccess(c.key as 'jobpilot' | 'evalpilot');
                      }}
                      className="inline-block text-[10px] mt-2 px-2 py-0.5 rounded font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
                    >
                      {requesting === c.key ? 'กำลังส่งคำขอ…' : verb}
                    </button>
                  );
                })()}

                {disabled ? (
                  <div className="text-xs text-amber-600 mt-3">เร็วๆ นี้ — ยังไม่ได้ตั้งค่า URL</div>
                ) : (
                  <div className="mt-3 flex items-center gap-1 text-sm font-medium text-brand-700 group-hover:underline">
                    เปิดแอป
                    {c.internal ? <ArrowRight className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                  </div>
                )}
              </article>
            );

            if (disabled) return <div key={c.key}>{inner}</div>;
            if (c.internal) {
              return (
                <Link key={c.key} to={c.href!} className="block">
                  {inner}
                </Link>
              );
            }
            return (
              <a key={c.key} href={c.href!} className="block">
                {inner}
              </a>
            );
          })}
        </div>

        {isOwner && (
          <div className="mt-10 pt-6 border-t border-slate-200">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">จัดการผู้ดูแล</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  เชิญ Owner / HR / Manager + บริหารสิทธิ์การใช้งานข้ามแอป
                </p>
              </div>
              <button
                onClick={() => setInviteOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg px-3 py-2 shadow-sm"
              >
                <UserPlus className="w-4 h-4" />
                เชิญผู้ดูแล
              </button>
            </div>
            <TeamDirectory />
          </div>
        )}
      </section>

      {inviteOpen && <InviteAdminModal onClose={() => setInviteOpen(false)} />}
    </main>
  );
}
